import React, {useCallback, useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import {reaction, runInAction} from 'mobx';
import ReactQuill, {Quill} from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import s from '../../s';
import {DefaultPCS, EP_DEFAULT_FIXED_SIZE, EP_SHORT_PLATE_SIDE_MARGIN_IN, EP_SNAP_SIZES, EPBorderType, EPMethod, EPSizeType, isEPCustomLike, renderSettings} from '../../rendering/render.def';
import {FONT_OPTIONS, FONT_FAMILY_MAP, FONT_SIZES, Font as EPFont, loadEPFont, loadEPFontsFromHTML} from './ep-extended/engraved-plate-extended.store';
import {isEPContentClippedForSize} from './ep-clip';
import {EP_FRAME_FONT_FAMILY, EP_INHERITED_LINE_HEIGHT, EP_SIZE_DEFAULT_LINE_SPACING, isEpShortPlateOverwrap, resolveEpLineSpacings, transformCqhToPt, transformFontSizePtToCqh} from './ep-content-utils';
import {dynamicMeasuredHeightInches, editorFaceInches} from './editor-face';

const toQuillFontValue = (label: string): string => {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

const QUILL_FONT_OPTIONS = FONT_OPTIONS.map(font => ({
    ...font,
    quillValue: toQuillFontValue(font.label),
}));

const QUILL_FONT_FAMILY_MAP = QUILL_FONT_OPTIONS.reduce<Record<string, string>>((acc, font) => {
    acc[font.quillValue] = FONT_FAMILY_MAP[font.value];
    return acc;
}, {});

const normalizeAlignmentForFrame = (html: string): string => {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const blockNodes = Array.from(wrapper.querySelectorAll<HTMLElement>('p, li, ol, ul, div'));
    const desiredAlign = new Map<HTMLElement, string>();

    blockNodes.forEach(node => {
        const cls = node.className || '';
        const inlineAlign = node.style.textAlign;
        let nextAlign = '';
        if (cls.includes('ql-align-center')) {
            nextAlign = 'center';
        } else if (cls.includes('ql-align-right')) {
            nextAlign = 'right';
        } else if (cls.includes('ql-align-justify')) {
            nextAlign = 'justify';
        } else if (inlineAlign === 'center' || inlineAlign === 'right' || inlineAlign === 'justify') {
            nextAlign = inlineAlign;
        }

        if (nextAlign) {
            desiredAlign.set(node, nextAlign);
        }
    });

    wrapper.querySelectorAll<HTMLElement>('*').forEach(node => {
        node.style.removeProperty('text-align');
    });

    desiredAlign.forEach((align, node) => {
        node.style.textAlign = align;
    });

    wrapper.querySelectorAll<HTMLOListElement | HTMLUListElement>('ol, ul').forEach(list => {
        const markerInside =
            list.style.textAlign === 'center' ||
            list.style.textAlign === 'right' ||
            Array.from(list.querySelectorAll<HTMLElement>('li')).some(li => {
                const cls = li.className || '';
                return li.style.textAlign === 'center' || li.style.textAlign === 'right' || cls.includes('ql-align-center') || cls.includes('ql-align-right');
            });

        if (markerInside) {
            list.style.listStylePosition = 'inside';
            list.style.paddingLeft = '0';
            list.style.marginLeft = '0';
        } else {
            list.style.removeProperty('list-style-position');
            list.style.removeProperty('padding-left');
            list.style.removeProperty('margin-left');
        }
    });

    wrapper.querySelectorAll('span.ql-cursor').forEach(el => el.remove());

    return wrapper.innerHTML;
};
const Parchment = Quill.import('parchment') as any;
const Block = Quill.import('blots/block') as any;
const Inline = Quill.import('blots/inline') as any;

const LINE_HEIGHT_OPTIONS = ['0.75', '0.85', '1', '1.15', '1.25', '1.5', '2'];

const LineHeightStyle = new Parchment.StyleAttributor('lineheight', 'line-height', {
    scope: Parchment.Scope.BLOCK,
    whitelist: LINE_HEIGHT_OPTIONS,
});

class IndentStyle extends Block {
    static blotName = 'indent';
    static tagName = 'p';

    static create(value) {
        const node = super.create();

        value = value > 0 ? value : 0;
        node.style.marginLeft = `${value * 0.75}rem`;

        return node;
    }

    static formats(node) {
        const margin = node.style.marginLeft;

        if (!margin) {
            return undefined;
        }

        const value = parseFloat(margin) / 0.75;

        return value > 0 ? value : undefined;
    }
}

class SizeStyle extends Inline {
    static blotName = 'size';
    static tagName = 'span';

    static create(value) {
        const node = super.create();
        const size = value ? `calc(${value} / var(--ep-h-pt) * 100cqh)` : 'calc(12 / var(--ep-h-pt) * 100cqh)';

        node.style.fontSize = size;

        queueMicrotask(() => {
            const parentP = node.closest('p');

            if (parentP) {
                parentP.style.fontSize = size;
            }
        });

        return node;
    }

    static formats(node) {
        const fontSize = node.style.fontSize;

        if (fontSize) {
            // New format: calc(X / var(--ep-h-pt) * 100cqh)
            const varMatch = fontSize.match(/calc\(\s*([\d.]+)\s*\/\s*var\(--ep-h-pt\)/);
            if (varMatch) return varMatch[1];

            // Old format: calc(X / 72 * 100cqh)
            const cqhMatch = fontSize.match(/calc\(\s*([\d.]+)\s*\/\s*72/);
            if (cqhMatch) return cqhMatch[1];
        }

        // Fallback: parse pt format (from saved content on initial load)
        if (fontSize && fontSize.endsWith('pt')) {
            return fontSize.replace('pt', '');
        }

        return fontSize;
    }
}

class FontStyle extends Inline {
    static blotName = 'font';
    static tagName = 'span';

    static create(value) {
        const node = super.create();
        const family = QUILL_FONT_FAMILY_MAP[value] ?? value;

        node.style.fontFamily = family;
        node.setAttribute('data-font', value);

        return node;
    }

    static formats(node) {
        return node.getAttribute('data-font');
    }
}

class ScriptStyle extends Inline {
    static blotName = 'script';
    static tagName = 'span';

    static create(value) {
        const node = super.create();

        if (value === 'super') {
            node.style.verticalAlign = 'super';
            node.style.fontSize = 'smaller';
        }

        return node;
    }

    static formats(node) {
        if (node.style.verticalAlign === 'super') {
            return 'super';
        }

        return undefined;
    }
}

const AlignStyle = Quill.import('attributors/style/align') as any;
Quill.register(AlignStyle, true);

Quill.register({'formats/font': FontStyle, 'formats/size': SizeStyle, 'formats/indent': IndentStyle, 'formats/script': ScriptStyle}, true);

Quill.register(LineHeightStyle, true);

const Font = Quill.import('formats/font') as any;

Font.whitelist = QUILL_FONT_OPTIONS.map(f => f.quillValue);
Quill.register(Font, true);

const sizeOptions = FONT_SIZES.map(f => String(f.value));

const modules = {
    toolbar: [[{font: QUILL_FONT_OPTIONS.map(f => f.quillValue)}], [{size: sizeOptions}], ['bold', 'italic', 'underline', {script: 'super'}], [{lineheight: LINE_HEIGHT_OPTIONS}], [{indent: '-1'}, {indent: '+1'}], [{align: []}]],
};

const formats = ['header', 'font', 'size', 'bold', 'italic', 'underline', 'script', 'lineheight', 'indent', 'align'];

// Gives every line without a line-height one, so the editor never stores a Custom/SingleDoc paragraph
// without it (a stored paragraph without its own lays out at the plate box's EP_INHERITED_LINE_HEIGHT).
// Rich-text paste and undoing past the typing default create such lines; each takes the nearest
// line's spacing (resolveEpLineSpacings), so a paste shows at the spacing of the text around it rather
// than at the plate size's default next to typed lines at 1. Silent: it is no edit of the shopper's,
// so it stays out of the undo history, and both callers serialize the editor right after.
const ensureEpLineHeights = (editor: Quill): void => {
    const lines = editor.getLines();
    const own = lines.map(line => (line.formats().lineheight ? String(line.formats().lineheight) : undefined));
    const spacings = resolveEpLineSpacings(own, s.ps.EPWidth, s.ps.EPHeight);
    lines.forEach((line, i) => {
        if (!own[i]) editor.formatLine(editor.getIndex(line), 1, 'lineheight', spacings[i], 'silent');
    });
};

export const EPEditor = observer(() => {
    const quillRef = useRef<ReactQuill | null>(null);
    const lastSnapSizeRef = useRef<{width: number; height: number} | null>(null);
    const appliedLineSpacingSizesRef = useRef<Set<string>>(new Set());
    const isSizeAutoSnap = s.pcs.EPSizeType === EPSizeType.AutoSnap;
    const initialSnapIdx = EP_SNAP_SIZES.findIndex(snap => snap.width === s.prod.EPWidth && snap.height === s.prod.EPHeight);
    const [selectedSnap, setSelectedSnap] = useState(initialSnapIdx >= 0 ? initialSnapIdx : 0);
    const selectedSnapRef = useRef<number>(initialSnapIdx >= 0 ? initialSnapIdx : 0);
    const [isOverflowing, setIsOverflowing] = useState(false);
    useEffect(() => {
        runInAction(() => {
            s.ui.ep_overflowing = isOverflowing;
        });
        return () => {
            runInAction(() => {
                s.ui.ep_overflowing = false;
            });
        };
    }, [isOverflowing]);
    const defaultFontSize = '20';
    const lastFormatRef = useRef<{font: string; size: string; align: string | false}>({font: toQuillFontValue('Roboto'), size: defaultFontSize, align: 'center'});
    const wasEmptyRef = useRef(!s.ps.EPContent);
    const isMountedRef = useRef(true);
    const isInitializedRef = useRef(false);
    const checkOverflowRef = useRef<() => void>(() => {});

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const styleId = 'quill-custom-fonts';

        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');

            style.id = styleId;
            style.innerHTML = `${QUILL_FONT_OPTIONS.map(font => {
                const fontFamily = QUILL_FONT_FAMILY_MAP[font.quillValue];

                return `.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="${font.quillValue}"]::before,
                        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="${font.quillValue}"]::before {
                            content: "${font.label}";
                            font-family: ${fontFamily};
                        }
                        .ql-font-${font.quillValue} {
                            font-family: ${fontFamily};
                        }`;
            }).join('\n')}
                ${FONT_SIZES.map(size => {
                    return `.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${size.value}"]::before,
                            .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${size.value}"]::before {
                                content: "${size.value}pt";
                            }`;
                }).join('\n')}
                .ql-snow .ql-picker.ql-lineheight .ql-picker-label::before {
                    content: "";
                    display: inline-block;
                    width: 18px;
                    height: 18px;
                    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 18'%3E%3Cpath d='M2.5 5L4 2.5 5.5 5' fill='none' stroke='%23444' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M2.5 13L4 15.5 5.5 13' fill='none' stroke='%23444' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cline x1='4' y1='3' x2='4' y2='15' stroke='%23444' stroke-width='1'/%3E%3Cline x1='7.5' y1='4' x2='15.5' y2='4' stroke='%23444' stroke-width='1.5' stroke-linecap='round'/%3E%3Cline x1='7.5' y1='9' x2='15.5' y2='9' stroke='%23444' stroke-width='1.5' stroke-linecap='round'/%3E%3Cline x1='7.5' y1='14' x2='15.5' y2='14' stroke='%23444' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat center / 16px 16px;
                    vertical-align: middle;
                }
                .ql-snow .ql-picker.ql-lineheight {
                    width: 80px;
                }
                .ql-snow .ql-picker.ql-lineheight .ql-picker-options {
                    min-width: 100px;
                }
                ${LINE_HEIGHT_OPTIONS.map(val => {
                    const labels = {'0.75': 'Tight', '0.85': 'Compact', '1': 'Single', '1.15': '1.15', '1.25': '1.25', '1.5': '1.5', '2': 'Double'};
                    const label = labels[val] || val;
                    return `.ql-snow .ql-picker.ql-lineheight .ql-picker-item[data-value="${val}"]::before {
                                content: "${label}";
                            }
                            .ql-snow .ql-picker.ql-lineheight .ql-picker-label[data-value="${val}"]::before {
                                content: "${label}";
                                background: none;
                                width: auto;
                                height: auto;
                            }`;
                }).join('\n')}
                .ql-editor {
                    font-family: ${FONT_FAMILY_MAP[1]};
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    line-height: ${EP_INHERITED_LINE_HEIGHT};
                }
                .ql-editor p {
                    margin: 0;
                }
                .ql-container.ql-snow {
                    border: none !important;
                }
            `;

            document.head.appendChild(style);
        }

        loadEPFont(EPFont.Roboto);

        // Extract font/size from existing content so we re-apply the correct
        // values instead of hard-coded defaults after Quill's lossy HTML parse.
        const savedContent = s.ps.EPContent;
        let initFont = toQuillFontValue('Roboto');
        let initSize = defaultFontSize;

        if (savedContent) {
            const tmp = document.createElement('div');
            tmp.innerHTML = savedContent;
            const firstFontSpan = tmp.querySelector('span[data-font]');
            const firstSizeSpan = tmp.querySelector<HTMLElement>('span[style*="font-size"]');

            if (firstFontSpan) {
                const fn = firstFontSpan.getAttribute('data-font');
                if (fn) initFont = fn;
            }
            if (firstSizeSpan) {
                const fs = firstSizeSpan.style.fontSize;
                if (fs && fs.endsWith('pt')) initSize = fs.replace('pt', '');
            }

            lastFormatRef.current.font = initFont;
            lastFormatRef.current.size = initSize;
        }

        if (quillRef.current) {
            const editor = quillRef.current.getEditor();

            editor.format('font', initFont);
            editor.format('size', initSize);
            editor.format('align', 'center');
            // Line-spacing typing default — but only for an EMPTY editor. With saved
            // content this stamps whichever paragraph holds the cursor (the first)
            // while the rest keep their stored spacing, so a plate whose lines all
            // carried the size default (applyDefaultLineSpacing) came back with line
            // 1 at '1' and the others at e.g. '1.5' — visibly asymmetric leading the
            // renderers then have to compensate for. Saved paragraphs keep their own
            // inline line-height through Quill's HTML parse; leave them alone.
            if (!savedContent) editor.format('lineheight', '1');

            // Re-apply per-segment font/size from the saved HTML so the Quill
            // Delta matches the original content (Quill's HTML parser can lose
            // the custom nested span blots during initialisation).
            if (savedContent) {
                const tmp2 = document.createElement('div');
                tmp2.innerHTML = savedContent;
                let offset = 0;

                for (const p of Array.from(tmp2.querySelectorAll('p'))) {
                    const walker = document.createTreeWalker(p, NodeFilter.SHOW_TEXT);
                    let textNode: Node | null;

                    while ((textNode = walker.nextNode())) {
                        const text = textNode.textContent || '';
                        if (text.length === 0) continue;

                        let font: string | undefined;
                        let size: string | undefined;
                        let el = textNode.parentElement;

                        while (el && el !== p) {
                            if (!font && el.hasAttribute('data-font')) {
                                font = el.getAttribute('data-font') || undefined;
                            }
                            if (!size && (el as HTMLElement).style.fontSize?.endsWith('pt')) {
                                size = (el as HTMLElement).style.fontSize.replace('pt', '');
                            }
                            el = el.parentElement;
                        }

                        if (font || size) {
                            const fmts: Record<string, string> = {};
                            if (font) fmts.font = font;
                            if (size) fmts.size = size;
                            editor.formatText(offset, text.length, fmts, 'silent');
                        }
                        offset += text.length;
                    }

                    offset += 1; // newline between paragraphs
                }

                // A paragraph saved without a line-height (carts and orders from before the editor
                // stored one on every line) showed at EP_INHERITED_LINE_HEIGHT on every surface, and
                // that is the plate the customer approved: keep it on those paragraphs. The first
                // edit would otherwise stamp the plate size's default on them (ensureEpLineHeights)
                // and quietly move their spacing. Silent: no edit of the shopper's.
                for (const line of editor.getLines()) {
                    if (!line.formats().lineheight) editor.formatLine(editor.getIndex(line), 1, 'lineheight', String(EP_INHERITED_LINE_HEIGHT), 'silent');
                }
            }
        }

        const lineHeightPicker = document.querySelector('.ql-picker.ql-lineheight');
        const onLineHeightPickerMouseDown = (e: Event) => {
            e.preventDefault();
        };
        if (lineHeightPicker) {
            lineHeightPicker.addEventListener('mousedown', onLineHeightPickerMouseDown);
            const label = lineHeightPicker.querySelector('.ql-picker-label');
            if (label) label.setAttribute('title', 'Line Spacing');
        }

        setHeight();
        setContainerStyle();
        render();

        // Allow EPContent writes only after all initial Quill events have settled.
        // Without this, Quill's initialization re-parses the HTML via onChange/syncFromEditor
        // and can strip custom font/size blot formatting from the stored EPContent.
        requestAnimationFrame(() => {
            isInitializedRef.current = true;
            // render() above ran s.pulse() before the s.ps.css reaction (the next effect) existed, so
            // nothing has checked the saved content for overflow: check it once its web fonts are in.
            // A reopened cart whose text runs past the plate otherwise keeps Add To Cart enabled until
            // the first edit.
            void (document.fonts?.ready ?? Promise.resolve()).then(() => requestAnimationFrame(() => checkOverflowRef.current()));
        });

        // Fonts that arrive after that check (loadEPFont adds a stylesheet, and a face loads when text
        // first needs it) and a plate panel that opens later (collapsed on CAF and narrow viewports, so
        // the editor has no layout to measure at mount) change what fits: check again.
        const onFontsLoaded = () => checkOverflowRef.current();
        document.fonts?.addEventListener?.('loadingdone', onFontsLoaded);
        const editorRoot = quillRef.current?.getEditor().root;
        const resizeObserver = editorRoot && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => requestAnimationFrame(() => checkOverflowRef.current())) : null;
        if (editorRoot && resizeObserver) resizeObserver.observe(editorRoot);

        return () => {
            document.fonts?.removeEventListener?.('loadingdone', onFontsLoaded);
            resizeObserver?.disconnect();
            if (lineHeightPicker) {
                lineHeightPicker.removeEventListener('mousedown', onLineHeightPickerMouseDown);
            }
        };
        // Mount-once Quill init; helpers read MobX/refs and must not re-run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setContainerStyle = useCallback(() => {
        const container = document.querySelector('.ql-container') as HTMLElement;
        if (!container) return;
        const editorElement = document.querySelector('.ql-editor') as HTMLElement;
        const isSingleDoc = s.pcs.EPMethodID === EPMethod.SingleDoc;
        const isCustom = isEPCustomLike(s.ps.EPMethodID);
        const isSizeDefault = s.pcs.EPSizeType === EPSizeType.Default;
        const isInnerAccent = s.pcs.EPBorderType === EPBorderType.InnerAccent;
        const isSizeAutoSnap = s.pcs.EPSizeType === EPSizeType.AutoSnap;
        const isSizeDynamic = s.pcs.EPSizeType === EPSizeType.Dynamic;
        const applyPadding = s.ps.EPHeight >= 1 || isSizeDynamic || isSingleDoc;
        const halfPad = applyPadding && s.ps.EPHeight < 1;
        const topPad = halfPad ? renderSettings.topPadding * 0.5 : renderSettings.topPadding;
        const sidePad = halfPad ? renderSettings.defaultPadding * 0.5 : renderSettings.defaultPadding;
        // Short plates (< 1", e.g. 3" x 0.5") reserve no vertical padding, but text
        // must still keep 0.25" clear of the plate's left/right edges.
        const padding = applyPadding ? `${topPad}px ${sidePad}px ${sidePad}px ${sidePad}px` : `0 ${EP_SHORT_PLATE_SIDE_MARGIN_IN * renderSettings.referenceDPI}px`;

        // Exact fractional px — flooring here (17 instead of 17.5 for a 0.5" plate)
        // shrinks the cqh-resolved fonts ~3% relative to the width, so borderline
        // text fits in the editor but wraps on the frame canvas. The canvas lays
        // text out in this same reference box (EPContent), so the two must agree
        // to the sub-pixel. Sizes are quantized to whole typographic points first —
        // the canvas builds its box from css.ep_plateWidthPt/HeightPt (rounded pt)
        // and --ep-h-pt is rounded pt on both surfaces, so a fractional Dynamic
        // height would otherwise resolve fonts ~0.5% differently here than there.
        // Snap sizes are all whole-pt already; this only affects Dynamic plates.
        const css = s.ps.css;
        const face = editorFaceInches({
            isSingleDoc,
            epWidth: s.ps.EPWidth,
            epHeight: s.ps.EPHeight,
            plateWidthPt: css?.ep_plateWidthPt,
            plateHeightPt: css?.ep_plateHeightPt,
            docWidth: s.ps.docWidth,
            docHeight: s.ps.docHeight,
        });
        const epWidth = `${(Math.round(face.width * 72) / 72) * renderSettings.referenceDPI}px`;
        const epHeight = `${(Math.round(face.height * 72) / 72) * renderSettings.referenceDPI}px`;

        const borderWidth = isInnerAccent ? 6 : 0;
        container.style.boxSizing = 'border-box';
        container.style.width = `calc(${epWidth} + ${borderWidth}px)`;
        container.style.padding = '0'; // padding on .ql-editor so cqh = full plate height
        container.style.overflow = 'clip';
        (container.style as any).overflowClipMargin = 'content-box';

        // Container-query sizing: enable cqh-based font scaling in preview
        container.style.containerType = 'size';
        container.style.setProperty('--ep-h-pt', String(Math.round(face.height * 72)));

        if (isCustom) {
            container.style.transform = 'scale(1.5)';
            container.style.transformOrigin = 'top center';
        }

        if (isInnerAccent) {
            container.style.border = '3px solid black';
            container.style.outline = '3px solid white';
        } else {
            container.style.boxShadow = `1px 1px 4px #000`;
        }

        if (editorElement) {
            editorElement.style.overflow = 'hidden';
            editorElement.style.color = s.ps.ep_id_view === 1099 ? 'white' : 'black';
            editorElement.style.padding = padding;
            editorElement.style.boxSizing = 'border-box';
        }

        // Dynamic used to leave height at Quill's `height: 100%`, which collapses when the
        // parent has no height. Lock it to the measured face, same as the other sizes.
        if (isSingleDoc || isSizeDefault || isSizeAutoSnap || isSizeDynamic) {
            container.style.height = `calc(${epHeight} + ${borderWidth}px)`;
            container.style.overflow = 'hidden';

            if (editorElement) {
                editorElement.style.height = '100%';
                editorElement.style.boxSizing = 'border-box';
            }
        }

        if (isCustom) {
            // transform: scale() doesn't affect layout flow, so reserve vertical space for the scaled content
            const rawHeightPx = container.getBoundingClientRect().height / 1.5;
            container.style.marginBottom = `${rawHeightPx * 0.5 + 10}px`;
        }
    }, []);

    useEffect(() => {
        const disposeCss = reaction(
            () => s.ps.css,
            () => {
                setContainerStyle();
                requestAnimationFrame(() => requestAnimationFrame(() => checkOverflowRef.current()));
            },
        );
        const disposeColor = reaction(
            () => s.ps.ep_id_view,
            () => {
                const editorElement = document.querySelector('.ql-editor') as HTMLElement;
                if (editorElement) {
                    editorElement.style.color = s.ps.ep_id_view === 1099 ? 'white' : 'black';
                }
            },
        );
        const disposeDocSize = reaction(
            () => ({w: s.ps.docWidth, h: s.ps.docHeight}),
            ({w, h}) => {
                const isCustom = isEPCustomLike(s.ps.EPMethodID);
                const isAutoSnap = s.pcs.EPSizeType === EPSizeType.AutoSnap;
                if (!isCustom) {
                    if (isAutoSnap && w > 0) {
                        const currentSnap = EP_SNAP_SIZES[selectedSnapRef.current];
                        if (currentSnap && currentSnap.width > w) {
                            const defaultIdx = EP_SNAP_SIZES.findIndex(snap => snap.width === EP_DEFAULT_FIXED_SIZE.width && snap.height === EP_DEFAULT_FIXED_SIZE.height);
                            applySnap(defaultIdx >= 0 ? defaultIdx : 0);
                        }
                    }
                    setHeight();
                } else if (isAutoSnap) {
                    // Custom (advanced) AutoSnap: keep the editor preview in sync with the
                    // frame when the document/frame size changes. The renderer (core.ts
                    // renderStandard* paths) clamps the rendered plate to a small 3" x 0.5"
                    // size for short documents (CurrentDocHeight <= 7) and cannot render a
                    // plate wider than the document. Mirror those constraints here so the
                    // editor shrinks/repositions along with the rendered plate instead of
                    // staying at the previously-selected larger size.
                    const currentSnap = EP_SNAP_SIZES[selectedSnapRef.current];
                    const SMALL_PLATE_IDX = 0; // EP_SNAP_SIZES[0] === {width: 3, height: 0.5}
                    if (h > 0 && h <= 7) {
                        if (selectedSnapRef.current !== SMALL_PLATE_IDX) applySnap(SMALL_PLATE_IDX);
                    } else if (currentSnap && w > 0 && currentSnap.width > w) {
                        const defaultIdx = EP_SNAP_SIZES.findIndex(snap => snap.width === EP_DEFAULT_FIXED_SIZE.width && snap.height === EP_DEFAULT_FIXED_SIZE.height);
                        applySnap(defaultIdx >= 0 ? defaultIdx : 0);
                    }
                }
                setTimeout(() => {
                    setContainerStyle();
                    requestAnimationFrame(() => requestAnimationFrame(() => checkOverflowRef.current()));
                }, 0);
            },
        );
        return () => {
            disposeCss();
            disposeColor();
            disposeDocSize();
        };
        // Mount-once MobX reactions; helpers read MobX/refs and must not re-subscribe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const parsePaddingPx = (paddingStr: string): {top: number; right: number; bottom: number; left: number} => {
        const parts = (paddingStr || '')
            .trim()
            .split(/\s+/)
            .map(p => parseFloat(p) || 0);
        if (parts.length === 4) return {top: parts[0], right: parts[1], bottom: parts[2], left: parts[3]};
        if (parts.length === 3) return {top: parts[0], right: parts[1], bottom: parts[2], left: parts[1]};
        if (parts.length === 2) return {top: parts[0], right: parts[1], bottom: parts[0], left: parts[1]};
        const v = parts[0] || 0;
        return {top: v, right: v, bottom: v, left: v};
    };

    const isContentClipped = useCallback((): boolean => {
        if (!quillRef.current) return false;
        const editor = quillRef.current.getEditor();
        const editorEl = editor.root as HTMLElement;

        const plainText = (editorEl.innerText || '').trim();
        if (!plainText) return false;

        const eps = 2;

        const isSingleDoc = s.pcs.EPMethodID === EPMethod.SingleDoc;
        if (isSingleDoc) {
            const css = s.ps.css || s.pcs.css;
            let epW = parseInt((css?.ep_width || '').replace(/[^0-9.-]/g, ''), 10);
            let epH = parseInt((css?.ep_height || '').replace(/[^0-9.-]/g, ''), 10);
            if (!(epW > 0 && epH > 0)) {
                epW = Math.floor((s.ps.EPWidth || 3) * renderSettings.referenceDPI);
                epH = Math.floor((s.ps.EPHeight || 2) * renderSettings.referenceDPI);
            }
            if (!(epW > 0 && epH > 0)) return false;

            const padRaw = css?.ep_padding;
            const pad = parsePaddingPx(padRaw && padRaw !== '0' ? padRaw : `${renderSettings.topPadding}px ${renderSettings.defaultPadding}px ${renderSettings.defaultPadding}px ${renderSettings.defaultPadding}px`);

            const contentW = Math.max(1, epW - pad.left - pad.right);
            const contentH = Math.max(1, epH - pad.top - pad.bottom);

            const mirror = document.createElement('div');
            const cs = getComputedStyle(editorEl);
            mirror.style.cssText = `position:absolute;left:-9999px;top:0;width:${contentW}px;max-width:${contentW}px;overflow:visible;visibility:hidden;pointer-events:none;box-sizing:border-box;font-family:${cs.fontFamily};font-size:${cs.fontSize};line-height:${EP_INHERITED_LINE_HEIGHT};white-space:pre-wrap;word-wrap:break-word;display:flex;flex-direction:column;justify-content:center;padding:0;`;
            mirror.innerHTML = editorEl.innerHTML;
            mirror.querySelectorAll('p, li').forEach(el => ((el as HTMLElement).style.margin = '0'));
            document.body.appendChild(mirror);
            const needH = mirror.scrollHeight;
            const needW = mirror.scrollWidth;
            document.body.removeChild(mirror);

            const measureTolerance = contentH < 80 ? 0 : Math.max(2, Math.min(18, Math.round(contentH * 0.02)));
            return needH > contentH + measureTolerance || needW > contentW + measureTolerance;
        }

        const availH = editorEl.clientHeight;
        const availW = editorEl.clientWidth;

        const overflowV = editorEl.scrollHeight - editorEl.clientHeight;
        const overflowH = editorEl.scrollWidth - editorEl.clientWidth;

        const rects: DOMRect[] = [];
        const walker = document.createTreeWalker(editorEl, NodeFilter.SHOW_TEXT);
        let node: Node | null;
        while ((node = walker.nextNode())) {
            const t = (node.textContent || '').trim();
            if (!t) continue;
            const range = document.createRange();
            range.selectNodeContents(node);
            for (const r of Array.from(range.getClientRects())) {
                if (r.width > 0.5 && r.height > 0.5) rects.push(r);
            }
        }

        const clip = editorEl.getBoundingClientRect();
        const contentTop = rects.length > 0 ? Math.min(...rects.map(r => r.top)) : 0;
        const contentBottom = rects.length > 0 ? Math.max(...rects.map(r => r.bottom)) : 0;
        const contentLeft = rects.length > 0 ? Math.min(...rects.map(r => r.left)) : 0;
        const contentRight = rects.length > 0 ? Math.max(...rects.map(r => r.right)) : 0;

        const epsClip = 3;
        if (rects.length > 0) {
            const fitsInClip = contentTop >= clip.top - epsClip && contentBottom <= clip.bottom + epsClip && contentLeft >= clip.left - epsClip && contentRight <= clip.right + epsClip;
            if (fitsInClip) return false;
        }

        const mirror = document.createElement('div');
        const cs = getComputedStyle(editorEl);
        // the editor's line-height as a factor: its computed value is px, which would reach every
        // paragraph without its own line-height as that fixed length
        mirror.style.cssText = `position:absolute;left:-9999px;top:0;width:${availW}px;overflow:visible;visibility:hidden;pointer-events:none;font-family:${cs.fontFamily};font-size:${cs.fontSize};line-height:${EP_INHERITED_LINE_HEIGHT};white-space:pre-wrap;word-wrap:break-word;`;
        mirror.innerHTML = editorEl.innerHTML;
        mirror.querySelectorAll('p, li').forEach(el => ((el as HTMLElement).style.margin = '0'));
        document.body.appendChild(mirror);
        const mirrorH = mirror.scrollHeight;
        const mirrorW = mirror.scrollWidth;
        document.body.removeChild(mirror);

        if (overflowV > eps || overflowH > eps) return true;
        if (rects.length === 0) return false;
        const clippedTop = contentTop < clip.top - eps;
        const clippedBottom = contentBottom > clip.bottom + eps;
        const clippedLeft = contentLeft < clip.left - eps;
        const clippedRight = contentRight > clip.right + eps;
        if (clippedTop || clippedBottom || clippedLeft || clippedRight) return true;
        return mirrorH > availH + eps || mirrorW > availW + eps;
    }, []);

    const checkOverflow = useCallback(() => {
        const isSizeDynamic = s.pcs.EPSizeType === EPSizeType.Dynamic;
        if (isSizeDynamic) {
            setIsOverflowing(false);
            return;
        }
        const editorEl = quillRef.current ? (quillRef.current.getEditor().root as HTMLElement) : null;
        // A collapsed plate panel (CAF and narrow viewports) leaves the editor with no layout to
        // measure, so the stored content is checked headlessly, as the CAF doc-size step checks it:
        // a reopened cart's overflow then blocks Add To Cart before the panel is ever opened.
        if (editorEl && editorEl.clientHeight === 0 && isEPCustomLike(s.ps.EPMethodID)) {
            const size = getEffectiveEPSizeForDoc(s.ps.docWidth, s.ps.docHeight);
            setIsOverflowing(isEPContentClippedForSize(s.ps.EPContent, size.width, size.height));
            return;
        }
        // On a 0.5"-high plate, >12pt text on 2+ lines warns even though nothing is
        // clipped — the live editor DOM carries the cqh font sizes and the laid-out
        // line boxes, so it feeds the shared rule directly.
        const overwrap = !!editorEl && isEpShortPlateOverwrap(editorEl.innerHTML, editorEl, s.ps.EPHeight);
        setIsOverflowing(overwrap || isContentClipped());
    }, [isContentClipped]);
    checkOverflowRef.current = checkOverflow;

    const setValue = useCallback((value: string) => {
        if (!isMountedRef.current || !isInitializedRef.current) return;
        const snapChanged = setHeight();

        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            ensureEpLineHeights(editor);
            value = editor.root.innerHTML;
            const isEmpty = editor.getText().trim().length === 0;

            if (!isEmpty) {
                if (wasEmptyRef.current) {
                    // Transitioning from empty to non-empty — pick up any format
                    // the user selected via the toolbar while the editor was empty
                    const len = editor.getLength() - 1;
                    if (len > 0) {
                        const appliedFmt = editor.getFormat(0, 1);
                        if (typeof appliedFmt.font === 'string') lastFormatRef.current.font = appliedFmt.font;
                        if (typeof appliedFmt.size === 'string') lastFormatRef.current.size = appliedFmt.size;
                        if (typeof appliedFmt.align === 'string') lastFormatRef.current.align = appliedFmt.align;
                        editor.formatText(0, len, {font: lastFormatRef.current.font, size: lastFormatRef.current.size}, 'silent');
                        editor.formatLine(0, len, 'align', lastFormatRef.current.align || false, 'silent');
                        value = editor.root.innerHTML;
                    }
                }
                const fmt = editor.getFormat() as Record<string, unknown>;
                if (typeof fmt.size === 'string') {
                    lastFormatRef.current.size = fmt.size;
                }
                if (typeof fmt.font === 'string') {
                    lastFormatRef.current.font = fmt.font;
                }
                lastFormatRef.current.align = typeof fmt.align === 'string' ? fmt.align : false;

                // Ensure all text has font and size format (fixes new lines losing font)
                const delta = editor.getContents();
                let idx = 0;
                let reformatted = false;

                for (const op of delta.ops || []) {
                    const text = typeof op.insert === 'string' ? op.insert : null;
                    const len = text ? text.length : 1;

                    if (text && text !== '\n') {
                        const missingFont = !op.attributes?.font;
                        const missingSize = !op.attributes?.size;

                        if (missingFont || missingSize) {
                            const formats: Record<string, string> = {};

                            if (missingFont) formats.font = lastFormatRef.current.font;
                            if (missingSize) formats.size = lastFormatRef.current.size;
                            editor.formatText(idx, len, formats, 'silent');
                            reformatted = true;
                        }
                    }

                    idx += len;
                }

                if (reformatted) {
                    value = editor.root.innerHTML;
                }

                // Force cursor to end of text after first character typed
                if (wasEmptyRef.current) {
                    setTimeout(() => {
                        if (isMountedRef.current && quillRef.current) {
                            const ed = quillRef.current.getEditor();
                            const len = ed.getLength() - 1;
                            ed.setSelection(len, 0);
                        }
                    }, 0);
                }
            }

            wasEmptyRef.current = isEmpty;
        }

        const normalizedValue = transformCqhToPt(normalizeAlignmentForFrame(value));
        s.ps.EPContent = normalizedValue;
        loadEPFontsFromHTML(normalizedValue);

        render();

        if (snapChanged || s.pcs.EPSizeType === EPSizeType.Dynamic) {
            setTimeout(() => setContainerStyle(), 0);
        }

        setTimeout(checkOverflow, 0);
        // Stable handler; helpers are also []-stable and read MobX/refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setHeight = useCallback((): boolean => {
        const isCustom = isEPCustomLike(s.ps.EPMethodID);
        const isSizeDefault = s.pcs.EPSizeType === EPSizeType.Default;
        const isSizeAutoSnap = s.pcs.EPSizeType === EPSizeType.AutoSnap;

        if (isCustom) {
            if (!quillRef.current) return false;

            const editor = quillRef.current.getEditor();

            if (isSizeDefault) {
                DefaultPCS.DefaultEPHeight = EP_DEFAULT_FIXED_SIZE.height;
                s.ps.EPHeight = EP_DEFAULT_FIXED_SIZE.height;
                s.ps.EPWidth = EP_DEFAULT_FIXED_SIZE.width;
            } else if (isSizeAutoSnap) {
                const snap = EP_SNAP_SIZES[selectedSnapRef.current];
                const {height, width} = snap;
                const lastSnap = lastSnapSizeRef.current;
                const snapChanged = !lastSnap || lastSnap.height !== height || lastSnap.width !== width;

                DefaultPCS.DefaultEPHeight = height;
                DefaultPCS.DefaultEPWidth = width;
                s.ps.EPHeight = height;
                s.ps.EPWidth = width;
                s.prod.EPHeight = height;
                s.prod.EPWidth = width;

                if (snapChanged) {
                    lastSnapSizeRef.current = {width, height};
                }

                return snapChanged;
            } else {
                // Measure the text, not the stretched box. A locked height would otherwise
                // report itself as the content and the plate would grow on every keystroke.
                const root = editor.root as HTMLElement;
                const lockedHeight = root.style.height;

                root.style.height = 'auto';
                const contentHeight = root.scrollHeight;

                root.style.height = lockedHeight;

                if (!(s.ps.EPWidth > 0)) {
                    s.ps.EPWidth = EP_DEFAULT_FIXED_SIZE.width;
                    s.prod.EPWidth = EP_DEFAULT_FIXED_SIZE.width;
                }

                const scrollHeightInDefault = dynamicMeasuredHeightInches(contentHeight);

                // No layout yet (the plate section starts collapsed on CAF and narrow viewports).
                // Keep the product height; the next edit measures the text.
                if (scrollHeightInDefault == null) {
                    return false;
                }

                DefaultPCS.DefaultEPHeight = scrollHeightInDefault;
                s.ps.EPHeight = scrollHeightInDefault;
            }
        }

        return false;
    }, []);

    const render = useCallback(() => {
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    }, []);

    const syncFromEditor = useCallback(() => {
        if (!isMountedRef.current || !quillRef.current || !isInitializedRef.current) return;

        const editor = quillRef.current.getEditor();
        ensureEpLineHeights(editor);
        const normalizedValue = transformCqhToPt(normalizeAlignmentForFrame(editor.root.innerHTML));

        if (normalizedValue === s.ps.EPContent) return;

        s.ps.EPContent = normalizedValue;
        loadEPFontsFromHTML(normalizedValue);
        render();
        setTimeout(checkOverflow, 0);
    }, [checkOverflow, render]);

    useEffect(() => {
        if (!quillRef.current) return;

        const editor = quillRef.current.getEditor();
        const onEditorChange = () => {
            setTimeout(syncFromEditor, 0);
        };

        editor.on('editor-change', onEditorChange);
        return () => {
            editor.off('editor-change', onEditorChange);
        };
    }, [syncFromEditor]);

    // Placeholder overlay — shows "Your Text Here" until user clicks/focuses/types in the editor
    useEffect(() => {
        const content = s.ps.EPContent || '';
        const tmp = document.createElement('div');
        tmp.innerHTML = content;
        if (tmp.textContent?.trim()) return;
        if (!quillRef.current) return;
        const editor = quillRef.current.getEditor();
        const container = editor.root.parentElement as HTMLElement;
        if (!container) return;

        container.style.position = 'relative';
        const placeholder = document.createElement('div');
        placeholder.style.cssText = `position:absolute;top:0;left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:10;color:#444;font-size:calc(${defaultFontSize} / var(--ep-h-pt) * 100cqh);font-family:${FONT_FAMILY_MAP[EPFont.Roboto]};text-align:center;`;
        placeholder.textContent = 'Your Text Here';
        container.appendChild(placeholder);

        let dismissed = false;
        const dismiss = () => {
            if (dismissed) return;
            dismissed = true;
            placeholder.remove();
            editor.root.removeEventListener('focus', dismiss);
            editor.root.removeEventListener('click', dismiss);
            editor.off('text-change', dismiss);
        };
        editor.root.addEventListener('focus', dismiss, {once: true});
        editor.root.addEventListener('click', dismiss, {once: true});
        editor.on('text-change', dismiss);

        return () => {
            dismiss();
        };
    }, []);

    const applyDefaultLineSpacing = useCallback((width: number, height: number) => {
        const isTargetMethod = s.ps.EPMethodID === EPMethod.Custom || s.pcs.EPMethodID === EPMethod.SingleDoc;
        if (!isTargetMethod || !quillRef.current) return;

        const key = `${width}x${height}`;
        const spacing = EP_SIZE_DEFAULT_LINE_SPACING[key];
        if (!spacing || appliedLineSpacingSizesRef.current.has(key)) return;
        appliedLineSpacingSizesRef.current.add(key);

        const editor = quillRef.current.getEditor();
        editor.formatLine(0, Math.max(editor.getLength(), 1), 'lineheight', spacing);
    }, []);

    const applySnap = useCallback((idx: number) => {
        selectedSnapRef.current = idx;
        setSelectedSnap(idx);

        const snap = EP_SNAP_SIZES[idx];

        DefaultPCS.DefaultEPHeight = snap.height;
        DefaultPCS.DefaultEPWidth = snap.width;
        s.ps.EPHeight = snap.height;
        s.ps.EPWidth = snap.width;
        s.prod.EPHeight = snap.height;
        s.prod.EPWidth = snap.width;
        lastSnapSizeRef.current = {width: snap.width, height: snap.height};

        applyDefaultLineSpacing(snap.width, snap.height);

        render();
        setTimeout(() => {
            setContainerStyle();
            checkOverflow();
        }, 0);
        // Stable handler; helpers are also []-stable and read MobX/refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <ReactQuill
                theme="snow"
                ref={quillRef}
                defaultValue={s.ps.EPContent || ''}
                onChange={setValue}
                modules={modules}
                formats={formats}
            />
            {isOverflowing && <div style={{color: '#c00', fontSize: '0.8rem', fontWeight: 600, textAlign: 'center', marginTop: 4}}>Text is being cut off. Reduce text or font size to fit within the plate.</div>}
            {isSizeAutoSnap && (
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12, marginBottom: 4}}>
                    <label
                        htmlFor="ep-plate-size"
                        style={{fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap'}}
                    >
                        Plate Size:
                    </label>
                    <select
                        id="ep-plate-size"
                        value={selectedSnap}
                        onChange={e => applySnap(Number(e.target.value))}
                        style={{fontSize: '0.875rem', padding: '4px 8px'}}
                        aria-label="Select personalized plate size"
                    >
                        {EP_SNAP_SIZES.map((snap, idx) => {
                            // A short document clamps the rendered plate to the smallest
                            // 3" x 0.5" size (see getEffectiveEPSizeForDoc / core.ts), so
                            // only offer that size when the document height is <= 7".
                            if (s.ps.docHeight > 0 && s.ps.docHeight <= 7 && idx !== 0) return null;
                            if (s.ps.docWidth > 0 && snap.width > s.ps.docWidth) return null;
                            return (
                                <option
                                    key={idx}
                                    value={idx}
                                >
                                    {snap.width}&quot; &times; {snap.height}&quot;
                                </option>
                            );
                        })}
                    </select>
                </div>
            )}
        </>
    );
});

export const EPApprovalPreview = observer(() => {
    const contentRef = useRef<HTMLDivElement>(null);

    // Use the effective (clamped) plate size so the approval proof matches the
    // size the frame actually renders for the current document size.
    const effSize = getEffectiveEPSizeForDoc(s.ps.docWidth, s.ps.docHeight);
    // Exact fractional px, quantized to whole pt — same reference geometry as the
    // editor and frame canvas; flooring would change which strings fit a line
    // (see setContainerStyle).
    const epWidthPx = (Math.round(effSize.width * 72) / 72) * renderSettings.referenceDPI;
    const epHeightPx = (Math.round(effSize.height * 72) / 72) * renderSettings.referenceDPI;
    const epHeightPt = Math.round(effSize.height * 72);
    const isInnerAccent = s.pcs.EPBorderType === EPBorderType.InnerAccent;
    const borderWidth = isInnerAccent ? 6 : 0;
    const isCustom = isEPCustomLike(s.ps.EPMethodID);

    const applyPadding = effSize.height >= 1;
    const topPad = applyPadding ? renderSettings.topPadding : 0;
    const sidePad = applyPadding ? renderSettings.defaultPadding : 0;
    // Short plates (< 1") keep 0.25" side margins even though they get no vertical padding.
    const padding = applyPadding ? `${topPad}px ${sidePad}px ${sidePad}px ${sidePad}px` : `0 ${EP_SHORT_PLATE_SIDE_MARGIN_IN * renderSettings.referenceDPI}px`;

    const previewHtml = transformFontSizePtToCqh(s.ps.EPContent || '');

    useEffect(() => {
        if (contentRef.current) {
            // Quill stores the chosen line spacing on each <p>; one stored without any takes the
            // content box's EP_INHERITED_LINE_HEIGHT, as on the frame canvas.
            contentRef.current.querySelectorAll('p').forEach(p => {
                p.style.margin = '0';
            });
        }
    });

    // Cap zoom so the plate never exceeds the available modal width (~650px usable).
    const maxContainerWidth = 650;
    const naturalWidth = epWidthPx + borderWidth;
    const desiredZoom = isCustom ? 2.5 : 1;
    const maxZoom = maxContainerWidth / naturalWidth;
    const zoom = Math.min(desiredZoom, maxZoom);

    return (
        <div style={{display: 'inline-block', zoom}}>
            <div
                style={{
                    width: naturalWidth,
                    height: epHeightPx + borderWidth,
                    containerType: 'size' as any,
                    boxSizing: 'border-box',
                    backgroundImage: s.pcs.css.ep_backgroundImage,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    border: isInnerAccent ? '3px solid black' : 'none',
                    outline: isInnerAccent ? '3px solid white' : 'none',
                    boxShadow: isInnerAccent ? 'none' : '1px 1px 4px #000',
                    overflow: 'hidden',
                    ['--ep-h-pt' as any]: String(epHeightPt),
                }}
            >
                <div
                    ref={contentRef}
                    dangerouslySetInnerHTML={{__html: previewHtml}}
                    style={{
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        padding,
                        color: s.pcs.css.ep_color || 'black',
                        // the frame canvas's family (the paragraph strut) and inherited line-height
                        fontFamily: EP_FRAME_FONT_FAMILY,
                        lineHeight: EP_INHERITED_LINE_HEIGHT,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        textAlign: 'center',
                        // match the editor/canvas layout mode so the proof wraps
                        // at the same points (preserved spaces, long words)
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                    }}
                />
            </div>
        </div>
    );
});

/**
 * Effective rendered engraved-plate size (in inches) for a given document size.
 * Mirrors the clamp in core.ts (the renderStandard* paths): a short document
 * (height <= 7") forces the plate down to the smallest 3" x 0.5" size. Callers
 * that live outside the renderer (e.g. the CAF add-to-cart step) use this to
 * reason about the size the frame will actually draw.
 */
export const getEffectiveEPSizeForDoc = (docWidthInches: number, docHeightInches: number): {width: number; height: number} => {
    let width = s.ps.EPWidth > 0 ? s.ps.EPWidth : DefaultPCS.DefaultEPWidth;
    let height = s.ps.EPHeight > 0 ? s.ps.EPHeight : DefaultPCS.DefaultEPHeight;

    // Keep in sync with core.ts: `if (CurrentDocHeight <= 7) { EPWidth = 3; EPHeight = 0.5; }`
    if (docHeightInches > 0 && docHeightInches <= 7) {
        width = EP_SNAP_SIZES[0].width; // 3"
        height = EP_SNAP_SIZES[0].height; // 0.5"
    }

    return {width, height};
};
